/**
 * React NodeView for `fileEmbed` on the card-description + comment editor
 * surfaces. Bound by `buildCardFileEmbedExtension` (registered when
 * `surface === 'card'` / `'comment'`).
 *
 * Compared to the wiki `FileEmbedView` this is intentionally lean: no
 * no `WikiEmbedOrchestrator` window events (cards + comments don't mount
 * that orchestrator). Its jobs are (1) an uploading chip (filename +
 * spinner) while the dropped / picked file uploads, and (2) a ready chip
 * (mime icon + filename + size) that opens the shared
 * `<AttachmentLightbox>` on click.
 *
 * Clicking used to resolve a signed URL and `window.open` it, which
 * bounced the reader out to a browser tab. It now opens the same lightbox
 * an attachment tile does, so every file on the card detail surface
 * behaves identically. Non-image files render as a glyph there, so the
 * signed URL is only resolved for images — a PDF or zip opens instantly.
 *
 * Branches, in priority order:
 *   0. FAILED — `uploadingFailed` is set on a placeholder. The chip stays
 *      in the body as an error state (filename + "Upload failed" + Retry /
 *      Dismiss) instead of vanishing; the buttons dispatch the card-embed
 *      window events and the OWNING composer (which retained the `File`)
 *      re-runs the upload against this same node or removes it.
 *   1. UPLOADING — `attachmentId` is null. Renders a spinner + filename.
 *   2. READY — `attachmentId` set. Renders the mime-icon chip + size;
 *      click opens the lightbox.
 */

import { NodeViewWrapper, type ReactNodeViewProps } from '@tiptap/react'
import { AlertCircle, Loader2, RotateCcw, X } from 'lucide-react'
import { type MouseEvent, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { AttachmentLightbox } from '@/components/AttachmentLightbox'
import { useToast } from '@/components/ToastProvider'
import {
  type CardEmbedUploadAction,
  dispatchCardEmbedUploadAction,
} from '@/editor/nodes/card-embed-upload-events'
import { formatBytes } from '@/features/chat/lib/format-bytes'
import { uploadPercent } from '@/features/uploads/UploadProgressBar'
import { resolveFileIcon } from '@/features/uploads/file-icon'
import { fetchSignedAttachmentUrl } from '@/features/uploads/use-file-upload'
import { useUploadProgress } from '@/features/wiki/hooks/use-upload-guards'
import { formatPercent } from '@/i18n/formatters'

export function CardFileEmbedView({ node }: ReactNodeViewProps<HTMLElement>) {
  const { t, i18n } = useTranslation('uploads')
  const toast = useToast()
  const attachmentId = (node.attrs.attachmentId as string | null) ?? null
  const fileName = (node.attrs.fileName as string) ?? ''
  const sizeBytes = (node.attrs.sizeBytes as number | null) ?? null
  const mimeType = (node.attrs.mimeType as string | null) ?? null
  const uploadingPlaceholderId = (node.attrs.uploadingPlaceholderId as string | null) ?? null
  // Byte progress arrives via the upload registry, keyed on the
  // placeholder id — the composer has no prop channel into a NodeView.
  // Null until the first byte event, so the chip shows the spinner alone
  // rather than a 0% that reads as stalled.
  const uploadPercentValue = uploadPercent(useUploadProgress(uploadingPlaceholderId))
  const uploadingFailed = Boolean(node.attrs.uploadingFailed)

  const Icon = useMemo(() => resolveFileIcon(mimeType, fileName), [mimeType, fileName])
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null)

  // Open-on-click. MUST live above the uploading early return so the hook
  // count stays stable across uploading ↔ ready renders (an upload
  // transitioning attachmentId `null` → `<uuid>` must not flip the hook
  // count mid-component-instance).
  //
  // The URL is resolved for EVERY type, not just images: a non-image
  // renders as a glyph, but its lightbox still offers "Open in new tab",
  // which is what keeps a PDF readable now that it no longer opens
  // directly. Skipping the fetch for non-images silently removed that
  // link — the whole reason it's there.
  const handleOpen = useCallback(async () => {
    if (!attachmentId) return
    try {
      setLightboxUrl(await fetchSignedAttachmentUrl(attachmentId))
      setLightboxOpen(true)
    } catch {
      toast.error(t('embed.fileDownloadError'))
    }
  }, [attachmentId, toast, t])

  // Shared by the failed branch (retry / dismiss) and the uploading one
  // (cancel). Plain inline function, no hook, so the hook count stays
  // stable across the uploading ↔ failed ↔ ready flips.
  const dispatchAction = (action: CardEmbedUploadAction) => (e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (!uploadingPlaceholderId) return
    dispatchCardEmbedUploadAction(action, uploadingPlaceholderId)
  }

  // --- 0. FAILED branch. Checked before UPLOADING — a failed chip still
  // has no `attachmentId` and still carries its placeholder id, which is
  // how the owning composer re-targets this same node on Retry. Plain
  // inline handlers, no new hooks, so the hook count stays stable across
  // the uploading ↔ failed ↔ ready flips.
  if (!attachmentId && uploadingPlaceholderId && uploadingFailed) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="card-file-embed"
        data-state="error"
        // `max-w-full` + a shrinkable filename. Nothing here could give
        // way before: the name had a fixed 18rem cap rather than a shrink,
        // the label wrapped to two lines, and both buttons held their
        // width — so in a narrow composer the row simply ran past the edge
        // and the controls needed a horizontal scroll to reach.
        className="relative inline-flex max-w-full items-center align-baseline"
        contentEditable={false}
      >
        {/* A plain span, NOT an `<output>`: this is the chip itself, and an
            implicit `role="status"` made a screen reader announce the whole
            chip body as a live message on top of the toast AND the
            composer's own sr-only region. The composer owns the
            announcement; this is content. */}
        <span
          className="wiki-inline-chip wiki-inline-chip--file inline-flex min-w-0 cursor-default items-center gap-1 border-danger/40 text-fg-muted"
          title={fileName}
        >
          <AlertCircle aria-hidden="true" size={12} className="shrink-0 text-danger" />
          {/* The one element that yields. `min-w-0` is required or a flex
           * child refuses to shrink below its content and truncate never
           * engages. */}
          <span className="min-w-0 flex-1 truncate">{fileName}</span>
          {/* One line, always: wrapped, it doubled the chip's height and
           * pushed the buttons further out of reach. */}
          <span className="shrink-0 whitespace-nowrap text-danger">{t('embed.failedLabel')}</span>
        </span>
        <button
          type="button"
          onClick={dispatchAction('retry')}
          onMouseDown={(e) => e.stopPropagation()}
          className="ms-1 inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-border bg-bg-elevated text-fg hover:bg-bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={t('embed.retryAria', { fileName })}
          title={t('embed.retry')}
        >
          <RotateCcw aria-hidden="true" size={12} />
        </button>
        <button
          type="button"
          onClick={dispatchAction('dismiss')}
          onMouseDown={(e) => e.stopPropagation()}
          className="ms-1 inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-border bg-bg-elevated text-fg hover:bg-danger hover:text-danger-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          aria-label={t('embed.dismissAria', { fileName })}
          title={t('embed.dismiss')}
        >
          <X aria-hidden="true" size={12} />
        </button>
      </NodeViewWrapper>
    )
  }

  // --- 1. UPLOADING branch — no attachmentId yet.
  if (!attachmentId) {
    return (
      <NodeViewWrapper
        as="span"
        data-block-type="card-file-embed"
        data-state="uploading"
        // `max-w-full` for the same reason as the failed row: a long name
        // plus a percentage plus the cancel button overruns a narrow
        // composer otherwise.
        //
        // `inline-flex` + `items-center`, not `inline-block`: as inline
        // siblings the chip and the button align on their BASELINES, and
        // the chip's padding pushes its baseline below the button's, so
        // the control sat visibly low. `align-baseline` stays on the
        // container so the whole thing still sits on the text's baseline.
        className="relative inline-flex max-w-full items-center align-baseline"
        contentEditable={false}
      >
        <span className="wiki-inline-chip wiki-inline-chip--file inline-flex min-w-0 cursor-default items-center">
          <Loader2
            aria-hidden="true"
            size={14}
            className="me-1 shrink-0 animate-spin text-fg-muted"
          />
          <span className="min-w-0 flex-1 truncate text-fg">
            {fileName || t('embed.uploadingFile')}
          </span>
          {/* A number, not a bar. The chip is one line of running text —
           * a 2px rule inside it reads as an underline, and there is no
           * vertical room for anything taller. */}
          {uploadPercentValue !== null ? (
            <span className="ms-1.5 shrink-0 tabular-nums text-fg-muted">
              {formatPercent(uploadPercentValue / 100, i18n.language)}
            </span>
          ) : null}
        </span>
        {/* Cancel, in the state where cancelling still means something.
         * The chip's neighbours here are Retry and Dismiss, which belong
         * to the FAILED branch and answer a different question — this one
         * stops a transfer that is still running. Without it the only way
         * out of a large upload was to wait for it. */}
        {uploadingPlaceholderId ? (
          <button
            type="button"
            onClick={dispatchAction('cancel')}
            onMouseDown={(e) => e.stopPropagation()}
            // Bordered and full-contrast, not a muted glyph. It sits at
            // the end of a running sentence, so without its own edge it
            // reads as punctuation rather than as the chip's one control.
            className="ms-1 inline-flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center rounded-full border border-border bg-bg-elevated text-fg hover:bg-danger hover:text-danger-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
            aria-label={t('embed.cancelAria', { fileName })}
            title={t('embed.cancel')}
          >
            <X aria-hidden="true" size={12} />
          </button>
        ) : null}
      </NodeViewWrapper>
    )
  }

  // --- 2. READY branch — click downloads. `onMouseDown.stopPropagation`
  // is load-bearing: ProseMirror's selection-on-mousedown for atomic nodes
  // runs at capture phase and intercepts click intent before React's
  // synthetic onClick fires; without it, clicking selects the node instead
  // of triggering the download.
  return (
    <NodeViewWrapper
      as="span"
      data-block-type="card-file-embed"
      data-attachment-id={attachmentId}
      data-state="ready"
      className="relative inline-block align-baseline"
    >
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          void handleOpen()
        }}
        onMouseDown={(e) => e.stopPropagation()}
        aria-label={t('embed.fileAriaLabel', { fileName })}
        title={fileName}
        className="wiki-inline-chip wiki-inline-chip--file"
      >
        <Icon aria-hidden="true" size={14} className="me-1 text-fg-muted" />
        <span className="max-w-[18rem] truncate text-fg">{fileName}</span>
        {sizeBytes !== null && (
          <span className="ms-1.5 shrink-0 text-[0.75em] text-fg-muted">
            {formatBytes(sizeBytes, i18n.language)}
          </span>
        )}
      </button>
      {lightboxOpen && (
        <AttachmentLightbox
          filename={fileName}
          contentType={mimeType}
          sizeBytes={sizeBytes}
          url={lightboxUrl}
          attachmentId={attachmentId}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </NodeViewWrapper>
  )
}

CardFileEmbedView.displayName = 'CardFileEmbedView'
